import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  MessageCircle, Phone, RefreshCw, UserCheck, User, AlertCircle,
  Search, ArrowLeft, Image as ImageIcon, Bot
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const fmtRel = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const AdminKollyChats = () => {
  const token = localStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [selectedPhone, setSelectedPhone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/whatsapp/admin/chats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days, search: search || undefined, limit: 300 },
      });
      setData(r.data);
    } catch (e) {
      toast({
        title: 'Failed to load chats',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [days, search, token]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    chats: data?.unique_chats ?? 0,
    messages: data?.total_messages ?? 0,
    inbound: data?.inbound_messages ?? 0,
    outbound: data?.outbound_messages ?? 0,
  }), [data]);

  return (
    <div data-testid="admin-kolly-chats-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircle className="text-[#25D366]" /> Kolly Chat History
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Every conversation that happened on your WhatsApp business number — inbound and outbound.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            data-testid="kolly-chats-window"
          >
            <option value={1}>Today</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, text…"
              className="pl-8 pr-3 py-2 w-64 border border-gray-300 rounded-lg text-sm"
              data-testid="kolly-chats-search"
            />
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="kolly-chats-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Unique chats" value={summary.chats} tint="emerald" testId="kpi-chats" />
        <Stat label="Total messages" value={summary.messages} tint="blue" testId="kpi-total" />
        <Stat label="Inbound" value={summary.inbound} tint="indigo" testId="kpi-inbound" />
        <Stat label="Outbound (Kolly)" value={summary.outbound} tint="amber" testId="kpi-outbound" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (data?.chats?.length || 0) === 0 ? (
        <Card className="p-10 text-center text-gray-500" data-testid="kolly-chats-empty">
          <MessageCircle size={36} className="mx-auto text-gray-300 mb-3" />
          <p>No chats in this window yet.</p>
          <p className="text-xs mt-1 text-gray-400">
            Messages to/from <strong>+91 98219 22296</strong> will appear here.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-[calc(100vh-280px)] min-h-[500px]">
          {/* Conversation list */}
          <div
            className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col ${
              selectedPhone ? 'hidden lg:flex' : 'flex'
            }`}
            data-testid="chat-list"
          >
            <div className="px-4 py-2 border-b bg-gray-50 text-xs text-gray-600 font-medium flex items-center justify-between">
              <span>{data.chats.length} conversations</span>
              <span className="text-gray-400">sorted by recent</span>
            </div>
            <div className="overflow-y-auto flex-1 divide-y">
              {data.chats.map((c) => (
                <ChatListItem
                  key={c.phone}
                  chat={c}
                  selected={selectedPhone === c.phone}
                  onClick={() => setSelectedPhone(c.phone)}
                />
              ))}
            </div>
          </div>

          {/* Transcript */}
          <div
            className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col ${
              selectedPhone ? 'flex' : 'hidden lg:flex'
            }`}
            data-testid="chat-transcript-pane"
          >
            {selectedPhone ? (
              <ChatTranscript
                phone={selectedPhone}
                token={token}
                onBack={() => setSelectedPhone(null)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center text-gray-400 p-8">
                <div>
                  <MessageCircle size={48} className="mx-auto text-gray-200 mb-3" />
                  <p className="text-sm">Select a conversation to read the full chat.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// -------- Conversation list item --------
const ChatListItem = ({ chat, selected, onClick }) => {
  const name =
    chat.matched_user?.name ||
    chat.matched_tenant?.name ||
    'Unknown sender';
  const initial = (name || '?').charAt(0).toUpperCase();
  const tags = [];
  if (chat.matched_user) tags.push({ t: chat.matched_user.role, cls: 'bg-blue-100 text-blue-700', icon: UserCheck });
  if (chat.matched_tenant) tags.push({ t: 'tenant', cls: 'bg-purple-100 text-purple-700', icon: User });
  if (!chat.matched_user && !chat.matched_tenant) tags.push({ t: 'not in DB', cls: 'bg-amber-100 text-amber-700', icon: AlertCircle });
  if (chat.kinds?.includes('image')) tags.push({ t: 'image', cls: 'bg-gray-100 text-gray-700', icon: ImageIcon });

  return (
    <button
      onClick={onClick}
      data-testid={`chat-row-${chat.phone}`}
      className={`w-full text-left px-3 py-3 hover:bg-emerald-50/50 transition-colors ${
        selected ? 'bg-emerald-50 border-l-4 border-emerald-500' : 'border-l-4 border-transparent'
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-gray-900 truncate text-sm">{name}</p>
            <span className="text-[10px] text-gray-500 whitespace-nowrap">{fmtRel(chat.last_at)}</span>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <Phone size={10} /> {chat.phone}
            <span className="ml-auto tabular-nums font-medium text-gray-700">
              {chat.count} msg{chat.count !== 1 ? 's' : ''}
            </span>
          </p>
          <p className="text-xs text-gray-600 truncate mt-1">
            {chat.last_direction === 'out' && <Bot size={10} className="inline mr-1 text-emerald-600" />}
            {chat.last_text || '—'}
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {tags.map((tag) => {
              const Icon = tag.icon;
              return (
                <span key={tag.t} className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded ${tag.cls}`}>
                  <Icon size={8} /> {tag.t}
                </span>
              );
            })}
            <span className="text-[9px] text-gray-500 ml-auto">
              ↓{chat.inbound} ↑{chat.outbound}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

// -------- Transcript pane --------
const ChatTranscript = ({ phone, token, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/whatsapp/admin/chat/${encodeURIComponent(phone)}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { limit: 500 },
    })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => toast({
        title: 'Failed to load transcript',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      }))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [phone, token]);

  const name =
    data?.matched_user?.name ||
    data?.matched_tenant?.name ||
    'Unknown sender';

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-white flex items-center gap-3">
        <button
          onClick={onBack}
          className="lg:hidden p-1 rounded hover:bg-emerald-100"
          data-testid="chat-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">
          {loading ? '…' : (name || '?').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 truncate" data-testid="chat-header-name">
            {loading ? 'Loading…' : name}
          </p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Phone size={10} /> {phone}
            {!loading && data?.matched_user && (
              <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-semibold">
                {data.matched_user.role}
              </span>
            )}
            {!loading && data?.matched_tenant && !data?.matched_user && (
              <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-semibold">
                tenant
              </span>
            )}
            {!loading && !data?.matched_user && !data?.matched_tenant && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-semibold">
                not in DB
              </span>
            )}
          </p>
        </div>
        <span className="text-xs text-gray-500 tabular-nums" data-testid="chat-msg-count">
          {loading ? '…' : `${data?.count ?? 0} messages`}
        </span>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
        style={{
          backgroundColor: '#efeae2',
          backgroundImage:
            'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
        }}
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (data?.messages?.length || 0) === 0 ? (
          <p className="text-center text-gray-400 text-sm py-10">No messages in this conversation.</p>
        ) : (
          data.messages.map((m, i) => <MessageBubble key={m.id || i} msg={m} />)
        )}
      </div>
    </>
  );
};

const MessageBubble = ({ msg }) => {
  const outbound = msg.direction === 'out';
  const isImage = msg.kind === 'image';
  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`} data-testid={`bubble-${msg.direction}`}>
      <div
        className={`max-w-[75%] rounded-lg px-3 py-2 shadow-sm ${
          outbound
            ? 'bg-[#d9fdd3] text-gray-900 rounded-br-none'
            : 'bg-white text-gray-900 rounded-bl-none'
        }`}
      >
        {outbound && (
          <p className="text-[10px] font-semibold text-emerald-700 mb-0.5 flex items-center gap-1">
            <Bot size={10} /> Kolly
          </p>
        )}
        {isImage && (
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
            <ImageIcon size={10} /> image
          </p>
        )}
        <p className="whitespace-pre-wrap break-words text-sm">{msg.text || '—'}</p>
        <p className="text-[10px] text-gray-500 mt-1 text-right tabular-nums">
          {fmtTime(msg.at)}
        </p>
      </div>
    </div>
  );
};

// -------- KPI card --------
const Stat = ({ label, value, tint, testId }) => {
  const tints = {
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
    blue: 'from-blue-50 to-blue-100 text-blue-700',
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-700',
    amber: 'from-amber-50 to-amber-100 text-amber-700',
  };
  return (
    <Card data-testid={testId} className={`p-4 border-0 bg-gradient-to-br ${tints[tint] || tints.blue}`}>
      <p className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</p>
      <p className="text-3xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
};

export default AdminKollyChats;
